import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { addDays, EVENT_LABELS, EXCEPTION_LABELS, fmtDate, fmtDateTime, type PhotoReportRow } from '@em/shared';
import { api, qk, useSettings } from '../../api';
import { useSession } from '../../auth/AuthContext';
import { downloadCsv, hrs, mondayOf, openDownload, todayIn } from '../../components/format';
import { useToast } from '../../components/Toast';
import { Button, Card, EmptyState, Field, Hours, Input, PageHeader, QueryState, Tabs } from '../../components/ui';

type Kind = 'hours' | 'cash' | 'exceptions' | 'roster' | 'photos' | 'jobs';

export function ReportsPage() {
  const session = useSession();
  const settings = useSettings();
  const tz = settings.data?.defaultTimezone ?? 'Australia/Sydney';
  const [kind, setKind] = useState<Kind>('hours');
  const [from, setFrom] = useState(() => addDays(mondayOf(todayIn(tz)), -7));
  const [to, setTo] = useState(() => todayIn(tz));
  const range = { from, to };
  const valid = !!from && !!to && from <= to;

  const items: { value: Kind; label: string }[] = [
    { value: 'hours', label: 'Hours by employee' },
    { value: 'cash', label: 'Cash hours' },
    { value: 'exceptions', label: 'Exceptions by type' },
    { value: 'roster', label: 'Roster vs actual' },
    { value: 'photos', label: 'Punch photos' },
    ...(session.features.jobCosting ? [{ value: 'jobs' as const, label: 'Job costing' }] : []),
  ];

  return (
    <>
      <PageHeader title="Reports" subtitle="Hours come from calculated timesheet lines, so they match what is exported." />
      <div className="filters">
        <Field label="From">
          <Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} max={to} />
        </Field>
        <Field label="To">
          <Input type="date" value={to} onChange={(e) => setTo(e.target.value)} min={from} />
        </Field>
        <div className="row" style={{ paddingBottom: 1 }}>
          <Button
            size="sm"
            onClick={() => {
              const m = mondayOf(todayIn(tz));
              setFrom(m);
              setTo(addDays(m, 6));
            }}
          >
            This week
          </Button>
          <Button
            size="sm"
            onClick={() => {
              const m = addDays(mondayOf(todayIn(tz)), -7);
              setFrom(m);
              setTo(addDays(m, 6));
            }}
          >
            Last week
          </Button>
          <Button
            size="sm"
            onClick={() => {
              const t = todayIn(tz);
              setFrom(`${t.slice(0, 7)}-01`);
              setTo(t);
            }}
          >
            Month to date
          </Button>
        </div>
      </div>
      <Tabs label="Report" items={items} value={kind} onChange={setKind} />
      {!valid ? (
        <Card>
          <EmptyState title="Choose a valid date range" />
        </Card>
      ) : kind === 'hours' ? (
        <HoursReport range={range} />
      ) : kind === 'cash' ? (
        <CashReport range={range} />
      ) : kind === 'exceptions' ? (
        <ExceptionsReport range={range} />
      ) : kind === 'roster' ? (
        <RosterReport range={range} />
      ) : kind === 'photos' ? (
        <PhotosReport range={range} tz={tz} />
      ) : (
        <JobsReport range={range} />
      )}
    </>
  );
}

type Range = { from: string; to: string };
const csvName = (k: string, r: Range) => `${k}-${r.from}-to-${r.to}.csv`;

function HoursReport({ range }: { range: Range }) {
  const q = useQuery({ queryKey: qk.report('hours', range), queryFn: () => api.reportHours(range) });
  return (
    <QueryState q={q} loadingLabel="Running report">
      {(rows) => {
        const cats = [...new Set(rows.flatMap((r) => Object.keys(r.byCategory)))].sort();
        const totals = Object.fromEntries(cats.map((c) => [c, rows.reduce((s, r) => s + (r.byCategory[c] ?? 0), 0)]));
        return (
          <Card
            tight
            title={`${rows.length} employee${rows.length === 1 ? '' : 's'}`}
            actions={
              <Button size="sm" disabled={!rows.length} onClick={() => downloadCsv(csvName('hours', range), ['Employee', ...cats, 'Worked hours'], rows.map((r) => [r.employeeName, ...cats.map((c) => r.byCategory[c] ?? 0), r.workedHours]))}>
                Download CSV
              </Button>
            }
          >
            {rows.length === 0 ? (
              <EmptyState title="No hours in this range" />
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      {cats.map((c) => (
                        <th key={c} className="num">
                          {c}
                        </th>
                      ))}
                      <th className="num">Worked hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <tr key={r.employeeId}>
                        <td className="strong">{r.employeeName}</td>
                        {cats.map((c) => (
                          <td key={c} className="num">
                            {r.byCategory[c] != null ? <Hours value={r.byCategory[c]} /> : <span className="muted">–</span>}
                          </td>
                        ))}
                        <td className="num strong">
                          <Hours value={r.workedHours} />
                        </td>
                      </tr>
                    ))}
                    <tr className="total">
                      <td>Total</td>
                      {cats.map((c) => (
                        <td key={c} className="num">
                          {hrs(Math.round(totals[c]! * 100) / 100)}
                        </td>
                      ))}
                      <td className="num">{hrs(Math.round(rows.reduce((s, r) => s + r.workedHours, 0) * 100) / 100)}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      }}
    </QueryState>
  );
}

/** Hours in categories marked "Paid in cash": left out of the MYOB export, listed here for whoever pays them. */
function CashReport({ range }: { range: Range }) {
  const q = useQuery({ queryKey: qk.report('cash', range), queryFn: () => api.reportCash(range) });
  return (
    <QueryState q={q} loadingLabel="Running report">
      {(rows) => {
        const total = Math.round(rows.reduce((s, r) => s + r.totalHours, 0) * 100) / 100;
        return (
          <Card
            tight
            title={`Cash hours: ${rows.length} employee${rows.length === 1 ? '' : 's'}, ${hrs(total)} in total`}
            actions={
              <Button
                size="sm"
                disabled={!rows.length}
                onClick={() =>
                  downloadCsv(
                    csvName('cash-hours', range),
                    ['Employee', 'Employee number', 'Date', 'Category', 'Hours'],
                    rows.flatMap((r) => r.days.map((d) => [r.employeeName, r.employeeNumber, d.date, d.categoryName, d.hours])),
                  )
                }
              >
                Download CSV
              </Button>
            }
          >
            {rows.length === 0 ? (
              <EmptyState title="No cash hours in this range">Hours land here when their payroll category is marked “Paid in cash” under Settings, Payroll categories.</EmptyState>
            ) : (
              <div className="table-wrap">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Employee</th>
                      <th>Date</th>
                      <th>Category</th>
                      <th className="num">Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r) => (
                      <Fragment key={r.employeeId}>
                        {r.days.map((d, i) => (
                          <tr key={`${d.date}-${d.category}`}>
                            <td className="strong">{i === 0 ? r.employeeName : ''}</td>
                            <td>{fmtDate(d.date)}</td>
                            <td>{d.categoryName}</td>
                            <td className="num">
                              <Hours value={d.hours} />
                            </td>
                          </tr>
                        ))}
                        <tr className="total">
                          <td colSpan={3}>{r.employeeName} total</td>
                          <td className="num">{hrs(r.totalHours)}</td>
                        </tr>
                      </Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        );
      }}
    </QueryState>
  );
}

function ExceptionsReport({ range }: { range: Range }) {
  const q = useQuery({ queryKey: qk.report('exceptions', range), queryFn: () => api.reportExceptions(range) });
  return (
    <QueryState q={q} loadingLabel="Running report">
      {(rows) => (
        <Card
          tight
          title="Exceptions by type (blocking and review)"
          actions={
            <Button size="sm" disabled={!rows.length} onClick={() => downloadCsv(csvName('exceptions', range), ['Type', 'Open', 'Resolved', 'Dismissed'], rows.map((r) => [EXCEPTION_LABELS[r.type], r.open, r.resolved, r.dismissed]))}>
              Download CSV
            </Button>
          }
        >
          {rows.length === 0 ? (
            <EmptyState title="No exceptions in this range" />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Type</th>
                  <th className="num">Open</th>
                  <th className="num">Resolved</th>
                  <th className="num">Dismissed</th>
                  <th className="num">Total</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.type}>
                    <td className="strong">{EXCEPTION_LABELS[r.type]}</td>
                    <td className={`num ${r.open ? 'red strong' : ''}`}>{r.open}</td>
                    <td className="num">{r.resolved}</td>
                    <td className="num">{r.dismissed}</td>
                    <td className="num">{r.open + r.resolved + r.dismissed}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </QueryState>
  );
}

function RosterReport({ range }: { range: Range }) {
  const q = useQuery({ queryKey: qk.report('roster', range), queryFn: () => api.reportRosterVsActual(range) });
  return (
    <QueryState q={q} loadingLabel="Running report">
      {(rows) => (
        <Card
          tight
          title="Roster vs actual"
          actions={
            <Button
              size="sm"
              disabled={!rows.length}
              onClick={() =>
                downloadCsv(
                  csvName('roster-vs-actual', range),
                  ['Employee', 'Rostered hours', 'Actual hours', 'Variance', 'Late starts', 'Early finishes', 'Absences'],
                  rows.map((r) => [r.employeeName, r.rosteredHours, r.actualHours, r.variance, r.lateStarts, r.earlyFinishes, r.absences]),
                )
              }
            >
              Download CSV
            </Button>
          }
        >
          {rows.length === 0 ? (
            <EmptyState title="No data in this range" />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th className="num">Rostered</th>
                  <th className="num">Actual</th>
                  <th className="num">Variance</th>
                  <th className="num">Late starts</th>
                  <th className="num">Early finishes</th>
                  <th className="num">Absences</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.employeeId}>
                    <td className="strong">{r.employeeName}</td>
                    <td className="num">
                      <Hours value={r.rosteredHours} />
                    </td>
                    <td className="num">
                      <Hours value={r.actualHours} />
                    </td>
                    <td className={`num ${r.variance > 0.25 ? 'amber' : r.variance < -0.25 ? 'red' : ''}`}>
                      {r.variance > 0 ? '+' : ''}
                      <Hours value={r.variance} />
                    </td>
                    <td className="num">{r.lateStarts}</td>
                    <td className="num">{r.earlyFinishes}</td>
                    <td className={`num ${r.absences ? 'red' : ''}`}>{r.absences}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </QueryState>
  );
}

const PHOTO_PAGE = 48;

function PhotosReport({ range, tz }: { range: Range; tz: string }) {
  const q = useQuery({ queryKey: qk.report('photos', range), queryFn: () => api.reportPhotos(range) });
  const [shown, setShown] = useState(PHOTO_PAGE);
  return (
    <QueryState q={q} loadingLabel="Loading photos">
      {(rows) => (
        <Card tight title={`${rows.length} punch photo${rows.length === 1 ? '' : 's'}`} actions={<span className="small muted">Viewing a photo is recorded in the audit log.</span>}>
          {rows.length === 0 ? (
            <EmptyState title="No photos in this range">Photos are taken at the kiosk when photo capture is on.</EmptyState>
          ) : (
            <div className="card-body stack">
              <div className="photo-grid">
                {rows.slice(0, shown).map((r) => (
                  <PhotoTile key={r.eventId} r={r} tz={tz} />
                ))}
              </div>
              {rows.length > shown && (
                <div className="row" style={{ justifyContent: 'center' }}>
                  <Button onClick={() => setShown((n) => n + PHOTO_PAGE)}>Show {Math.min(PHOTO_PAGE, rows.length - shown)} more</Button>
                </div>
              )}
            </div>
          )}
        </Card>
      )}
    </QueryState>
  );
}

/** Each tile signs its own short-lived image link, so only visible photos are requested. */
function PhotoTile({ r, tz }: { r: PhotoReportRow; tz: string }) {
  const toast = useToast();
  const src = useQuery({ queryKey: ['photo-url', r.eventId], queryFn: () => api.downloadUrl(r.photoUrl), staleTime: 90_000, gcTime: 90_000 });
  const label = `${r.employeeName}, ${EVENT_LABELS[r.type]} ${fmtDateTime(r.occurredAt, tz)}`;
  return (
    <figure className="photo" style={{ margin: 0 }}>
      <button className="btn ghost" style={{ padding: 0, height: 'auto', width: '100%', display: 'block' }} onClick={() => void openDownload(r.photoUrl, toast.error)} aria-label={`Open photo: ${label}`}>
        {src.data ? <img src={src.data} alt={label} loading="lazy" /> : <div style={{ aspectRatio: '3 / 4', background: 'var(--grey-bg)' }} />}
      </button>
      <figcaption className="meta">
        <strong>{r.employeeName}</strong>
        <div className="muted">
          {EVENT_LABELS[r.type]} · {fmtDateTime(r.occurredAt, tz)}
        </div>
        {r.siteName && <div className="muted">{r.siteName}</div>}
        {src.isError && <div className="red">Could not load</div>}
      </figcaption>
    </figure>
  );
}

function JobsReport({ range }: { range: Range }) {
  const q = useQuery({ queryKey: qk.report('jobs', range), queryFn: () => api.reportJobs(range) });
  const [open, setOpen] = useState<string | null>(null);
  return (
    <QueryState q={q} loadingLabel="Running report">
      {(rows) => (
        <Card
          tight
          title="Hours by job"
          actions={
            <Button
              size="sm"
              disabled={!rows.length}
              onClick={() =>
                downloadCsv(
                  csvName('jobs', range),
                  ['Job code', 'Job', 'Employee', 'Hours', 'Budget hours'],
                  rows.flatMap((j) => j.byEmployee.map((e) => [j.jobCode ?? '', j.jobName, e.employeeName, e.hours, j.budgetHours ?? ''])),
                )
              }
            >
              Download CSV
            </Button>
          }
        >
          {rows.length === 0 ? (
            <EmptyState title="No job hours in this range" />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Job</th>
                  <th className="num">Hours</th>
                  <th className="num">Budget</th>
                  <th className="num">Used</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {rows.map((j) => {
                  const key = j.jobId ?? 'none';
                  const pct = j.budgetHours ? Math.round((j.hours / j.budgetHours) * 100) : null;
                  return (
                    <Fragment key={key}>
                      <tr>
                        <td className="strong">
                          {j.jobCode && <span className="mono">{j.jobCode} </span>}
                          {j.jobName}
                        </td>
                        <td className="num">
                          <Hours value={j.hours} />
                        </td>
                        <td className="num">{j.budgetHours != null ? hrs(j.budgetHours) : '–'}</td>
                        <td className={`num ${pct != null && pct > 100 ? 'red strong' : pct != null && pct > 85 ? 'amber' : ''}`}>{pct != null ? `${pct}%` : '–'}</td>
                        <td className="actions">
                          <Button size="sm" variant="ghost" onClick={() => setOpen(open === key ? null : key)} aria-expanded={open === key}>
                            {open === key ? 'Hide' : 'By employee'}
                          </Button>
                        </td>
                      </tr>
                      {open === key &&
                        j.byEmployee.map((e) => (
                          <tr key={`${key}-${e.employeeId}`}>
                            <td style={{ paddingLeft: 32 }}>{e.employeeName}</td>
                            <td className="num">
                              <Hours value={e.hours} />
                            </td>
                            <td colSpan={3} />
                          </tr>
                        ))}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </QueryState>
  );
}

